import { useEffect, useRef, useState } from 'react'
import { Button, InputNumber, Space, Typography } from 'antd'
import { AimOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

export interface LocationValue {
  lat: number
  lng: number
}

interface LocationPickerProps {
  value?: LocationValue | null
  onChange?: (value: LocationValue | null) => void
  /** Joy tanlanmagan bo'lsa xarita shu nuqtadan ochiladi */
  fallbackCenter: LocationValue
  disabled?: boolean
  readOnly?: boolean
  height?: number
}

const TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'

function round(n: number): number {
  return Math.round(n * 1e6) / 1e6
}

export function LocationPicker({
  value,
  onChange,
  fallbackCenter,
  disabled,
  readOnly,
  height = 260,
}: LocationPickerProps) {
  const { t } = useTranslation()
  const holder = useRef<HTMLDivElement>(null)
  const map = useRef<L.Map | null>(null)
  const marker = useRef<L.CircleMarker | null>(null)
  const change = useRef(onChange)
  const locked = useRef(Boolean(disabled || readOnly))
  const [locating, setLocating] = useState(false)
  const [geoError, setGeoError] = useState<string | null>(null)

  change.current = onChange
  locked.current = Boolean(disabled || readOnly)

  useEffect(() => {
    if (!holder.current) return
    const start = value ?? fallbackCenter
    const m = L.map(holder.current, { attributionControl: true }).setView(
      [start.lat, start.lng],
      value ? 16 : 11,
    )
    L.tileLayer(TILES, {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap',
    }).addTo(m)
    m.on('click', (e: L.LeafletMouseEvent) => {
      if (locked.current) return
      change.current?.({ lat: round(e.latlng.lat), lng: round(e.latlng.lng) })
    })
    map.current = m
    const resize = window.setTimeout(() => m.invalidateSize(), 200)
    return () => {
      window.clearTimeout(resize)
      m.remove()
      map.current = null
      marker.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const m = map.current
    if (!m) return
    if (!value) {
      marker.current?.remove()
      marker.current = null
      return
    }
    const point: L.LatLngExpression = [value.lat, value.lng]
    if (marker.current) marker.current.setLatLng(point)
    else
      marker.current = L.circleMarker(point, {
        radius: 9,
        color: '#d4380d',
        weight: 3,
        fillOpacity: 0.6,
      }).addTo(m)
    if (!m.getBounds().contains(point)) m.setView(point, Math.max(m.getZoom(), 15))
  }, [value])

  const locate = () => {
    if (!navigator.geolocation) {
      setGeoError(t('customer.profile.geoUnavailable'))
      return
    }
    setLocating(true)
    setGeoError(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false)
        const next = { lat: round(pos.coords.latitude), lng: round(pos.coords.longitude) }
        change.current?.(next)
        map.current?.setView([next.lat, next.lng], 17)
      },
      () => {
        setLocating(false)
        setGeoError(t('customer.profile.geoDenied'))
      },
      { enableHighAccuracy: true, timeout: 15000 },
    )
  }

  const setPart = (part: 'lat' | 'lng', n: number | null) => {
    if (n === null) {
      onChange?.(null)
      return
    }
    const base = value ?? fallbackCenter
    onChange?.({ ...base, [part]: n })
  }

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <div
        ref={holder}
        style={{ height, width: '100%', borderRadius: 8, overflow: 'hidden', zIndex: 0 }}
      />
      {readOnly ? null : (
        <>
          <Space wrap>
            <InputNumber
              value={value?.lat ?? null}
              onChange={(n) => setPart('lat', n)}
              min={-90}
              max={90}
              step={0.000001}
              placeholder={t('customer.profile.latitude')}
              disabled={disabled}
              style={{ width: 160 }}
            />
            <InputNumber
              value={value?.lng ?? null}
              onChange={(n) => setPart('lng', n)}
              min={-180}
              max={180}
              step={0.000001}
              placeholder={t('customer.profile.longitude')}
              disabled={disabled}
              style={{ width: 160 }}
            />
            <Button icon={<AimOutlined />} onClick={locate} loading={locating} disabled={disabled}>
              {t('customer.profile.myLocation')}
            </Button>
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {geoError ?? t('customer.profile.mapHint')}
          </Typography.Text>
        </>
      )}
    </Space>
  )
}
